import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ActivityType,
  EmailBillingMode,
  ResolveRateQuery,
  ResolveRateResponse,
  UnitType,
} from '@packages/types';
import { queryKeys } from '@packages/api';
import { resolveRate } from '@packages/api/endpoints/account.js';
import { formatMinorUnitsAsDecimal, possessive } from '@packages/format';
import type { CopyFn } from '@packages/copy';
import { parseRateInputToMinorUnits } from './ActivityWizardForm.helpers';

// The activity form's default rate. The backend resolver decides the rate and
// where it came from; this file only decides WHICH entries ask it, and when
// its answer may overwrite what is in the rate field.

type EmailRateSettings = {
  emailBillingMode?: EmailBillingMode | null;
  defaultEmailRateMinorUnits?: number | null;
};

type EmailRateMember = { emailRateMinorUnits?: number | null };

/** Whether a flat per-email rate is set for this performer on a FIXED_RATE account. */
export function emailFixedRateConfigured(
  settings: EmailRateSettings | null | undefined,
  member: EmailRateMember | null | undefined,
): boolean {
  if (settings?.emailBillingMode !== 'FIXED_RATE') return false;
  return (member?.emailRateMinorUnits ?? settings.defaultEmailRateMinorUnits ?? 0) > 0;
}

export type DefaultRateRequest = Pick<ResolveRateQuery, 'unitType' | 'activityType'>;

/**
 * What to ask the resolver for this (type, unit) pair, or null when the entry
 * has no default rate at all and the field is left for the user to fill in.
 *
 * The gate is narrower than the resolver: the backend would answer an hourly
 * rate for items, documents and lump sums too, which the form must not
 * pre-fill. `activityType` is sent only for a flat-rate email billed by items
 * or lump sum, because the route refuses it for any other unit.
 */
export function defaultRateRequest(
  type: ActivityType | null | undefined,
  unitType: UnitType | undefined,
  settings: EmailRateSettings | null | undefined,
  member: EmailRateMember | null | undefined,
): DefaultRateRequest | null {
  if (!unitType) return null;
  if (unitType === 'hours' || unitType === 'pages' || unitType === 'km') return { unitType };
  if (
    type === 'EMAIL' &&
    (unitType === 'items' || unitType === 'lump_sum') &&
    emailFixedRateConfigured(settings, member)
  ) {
    return { unitType, activityType: 'EMAIL' };
  }
  return null;
}

/**
 * The line under the rate field: where the rate in the field came from, or
 * "Custom rate" when it is not the resolved one. Null when there is nothing
 * true to say - an empty field, or no answer from the resolver.
 */
export function describeRateHint(
  copy: CopyFn,
  input: {
    resolved: ResolveRateResponse | null;
    enteredRateMinorUnits: number | null;
    unitType: UnitType;
    performerName: string | undefined;
  },
): string | null {
  const { resolved, enteredRateMinorUnits, performerName } = input;
  if (!resolved || !enteredRateMinorUnits) return null;
  if (resolved.source === 'none' || enteredRateMinorUnits !== resolved.ratePerUnitMinorUnits) {
    return 'Custom rate';
  }

  if (resolved.source === 'client-project') return copy('activity:wizard.rateHint.sharedProject');
  if (resolved.source === 'client') return copy('activity:wizard.rateHint.sharedClient');
  if (resolved.source === 'account') return copy('activity:wizard.rateHint.account');

  // The remaining sources are the performer's own, so the hint names them. A
  // performer who has left the account has no name to show.
  if (!performerName) return null;
  const feeEarner = possessive(performerName);
  if (resolved.source === 'fee-earner-client-project') {
    return copy('activity:wizard.rateHint.feeEarnerProject', { feeEarner });
  }
  if (resolved.source === 'fee-earner-client') {
    return copy('activity:wizard.rateHint.feeEarnerClient', { feeEarner });
  }
  return copy('activity:wizard.rateHint.team', { feeEarner });
}

/**
 * What the next resolver answer may do to the rate field: replace it, or
 * replace it only while it still holds a given rate.
 */
type PendingReprice = 'follow' | { ifEquals: number } | null;

interface UseActivityDefaultRateInput {
  accountId: string | null | undefined;
  clientProjectId: string;
  /** The selected performer's user id; undefined when the entry has none. */
  performedByUserId: string | undefined;
  type: ActivityType | null | undefined;
  unitType: UnitType | undefined;
  practiceSettings: EmailRateSettings | null | undefined;
  performer: EmailRateMember | null | undefined;
  isEdit: boolean;
  /** Stable accessors for the rate field (wrap in useCallback). */
  getRate: () => string;
  setRate: (value: string) => void;
}

interface ActivityDefaultRate {
  /** The resolver's answer for the current entry; null while loading, on failure, or when nothing is asked. */
  resolved: ResolveRateResponse | null;
  /** Whether this entry has a default rate at all. */
  hasDefaultSource: boolean;
  /**
   * The resolver answered, and with no rate above zero. That is source `none`,
   * and also a practice-wide per-page or per-km rate left at 0, which resolves
   * as source `account`.
   */
  resolvedNoRate: boolean;
  /** True while the first answer for the current entry is on its way. */
  isResolving: boolean;
  /** Fill from the next resolver answer unless the user edits the field while waiting. */
  followNextRate: () => void;
  resolveFailed: boolean;
  retry: () => void;
}

/**
 * Pre-fills the rate field from the backend resolver.
 *
 * A new entry follows the resolver whenever its type, unit or performer
 * changes. A saved entry keeps its stored rate, with one exception: changing
 * the performer re-prices it when the field still holds the outgoing
 * performer's resolved rate, which is the only sign it was never amended by
 * hand. An answer of zero never overwrites the field.
 *
 * A failed resolve leaves the field as it is and editable. That includes a
 * response the client cannot parse, which a freshly loaded bundle can meet
 * while an older backend revision is still serving during a deploy.
 */
export function useActivityDefaultRate({
  accountId,
  clientProjectId,
  performedByUserId,
  type,
  unitType,
  practiceSettings,
  performer,
  isEdit,
  getRate,
  setRate,
}: UseActivityDefaultRateInput): ActivityDefaultRate {
  const queryClient = useQueryClient();
  const request = defaultRateRequest(type, unitType, practiceSettings, performer);
  const requestUnitType = request?.unitType;
  const requestActivityType = request?.activityType;
  const canResolve = !!accountId && !!clientProjectId && !!requestUnitType;

  const { data, isError, refetch } = useQuery({
    queryKey: queryKeys.resolvedRate(accountId ?? '', {
      clientProjectId,
      performedByUserId,
      unitType: requestUnitType ?? '',
      activityType: requestActivityType,
    }),
    queryFn: () =>
      resolveRate(accountId!, {
        clientProjectId,
        performedByUserId,
        unitType: requestUnitType!,
        activityType: requestActivityType,
      }),
    enabled: canResolve,
  });
  const resolved = canResolve && !isError && data ? data : null;

  const pendingRef = useRef<PendingReprice>(null);
  const followValueRef = useRef('');
  // The rate this hook last wrote for the current entry. A fresher answer for
  // the same entry replaces it only while the field still holds it.
  const appliedRef = useRef<number | null>(null);
  const previousRef = useRef({ performedByUserId, type, unitType, clientProjectId });

  const followNextRate = useCallback(() => {
    pendingRef.current = 'follow';
    followValueRef.current = getRate();
  }, [getRate]);

  useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = { performedByUserId, type, unitType, clientProjectId };
    const performerChanged = previous.performedByUserId !== performedByUserId;
    const unitOrTypeChanged = previous.unitType !== unitType || previous.type !== type;
    const matterChanged = previous.clientProjectId !== clientProjectId;
    const previousApplied = appliedRef.current;
    if (performerChanged || unitOrTypeChanged || matterChanged) {
      appliedRef.current = null;
    }

    const waiting = pendingRef.current;
    if (!isEdit && unitOrTypeChanged && unitType) {
      setRate('');
      pendingRef.current = requestUnitType ? 'follow' : null;
      followValueRef.current = ''; 
    } else if (matterChanged && type && !isEdit) {
      const fieldValue = getRate();
      const automaticRate = previousApplied ?? (waiting !== null && waiting !== 'follow' ? waiting.ifEquals : null);
      if (automaticRate !== null && parseRateInputToMinorUnits(fieldValue) === automaticRate) {
        pendingRef.current = { ifEquals: automaticRate };
      } else if (fieldValue === '') {
        pendingRef.current = 'follow';
        followValueRef.current = fieldValue;
      } else {
        pendingRef.current = null;
      }
    } else if (performerChanged && type && !isEdit) {
      pendingRef.current = 'follow';
      followValueRef.current = getRate();
    } else if (performerChanged && type && waiting !== 'follow') {
      const outgoing =
        accountId && requestUnitType
          ? queryClient.getQueryData<ResolveRateResponse>(
              queryKeys.resolvedRate(accountId, {
                clientProjectId,
                performedByUserId: previous.performedByUserId,
                unitType: requestUnitType,
                activityType: requestActivityType,
              }),
            )
          : undefined;
      const fieldRate = parseRateInputToMinorUnits(getRate());
      if (outgoing && outgoing.ratePerUnitMinorUnits > 0 && fieldRate === outgoing.ratePerUnitMinorUnits) {
        pendingRef.current = { ifEquals: outgoing.ratePerUnitMinorUnits };
      } else if (waiting === null || fieldRate !== waiting.ifEquals) {
        // A switch made while an earlier answer is still loading keeps waiting
        // on the rate the field held before it; anything else is hand-typed.
        pendingRef.current = null;
      }
    }

    const pending = pendingRef.current;
    if (pending !== null) {
      if (!canResolve) {
        pendingRef.current = null;
        return;
      }
      if (isError || !data) return;
      pendingRef.current = null;
      if (data.ratePerUnitMinorUnits <= 0) return;
      if (pending === 'follow' && getRate() !== followValueRef.current) return;
      if (pending !== 'follow' && parseRateInputToMinorUnits(getRate()) !== pending.ifEquals) return;
      setRate(formatMinorUnitsAsDecimal(data.ratePerUnitMinorUnits));
      appliedRef.current = data.ratePerUnitMinorUnits;
      return;
    }

    const applied = appliedRef.current;
    if (
      applied !== null &&
      resolved &&
      resolved.ratePerUnitMinorUnits > 0 &&
      resolved.ratePerUnitMinorUnits !== applied &&
      parseRateInputToMinorUnits(getRate()) === applied
    ) {
      setRate(formatMinorUnitsAsDecimal(resolved.ratePerUnitMinorUnits));
      appliedRef.current = resolved.ratePerUnitMinorUnits;
    }
  }, [
    accountId,
    canResolve,
    clientProjectId,
    data,
    getRate,
    isEdit,
    isError,
    performedByUserId,
    queryClient,
    requestActivityType,
    requestUnitType,
    resolved,
    setRate,
    type,
    unitType,
  ]);

  return {
    resolved,
    hasDefaultSource: request !== null,
    resolvedNoRate: resolved !== null && resolved.ratePerUnitMinorUnits <= 0,
    isResolving: canResolve && !isError && !data,
    followNextRate,
    resolveFailed: canResolve && isError,
    retry: () => { void refetch(); },
  };
}
